import React, { useState, useEffect, useRef } from 'react';
import './UploadImageModal.css';

interface UploadImageModalProps {
  isOpen: boolean;
  characterName: string;
  initialFile?: File;
  onUpload: (file: File) => Promise<void>;
  onCancel: () => void;
}

const UploadImageModal: React.FC<UploadImageModalProps> = ({
  isOpen,
  characterName,
  initialFile,
  onUpload,
  onCancel
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string>('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!isOpen) {
      // Reset state when modal closes
      setSelectedFile(null);
      setPreviewUrl('');
      setUploadProgress(0);
      setError('');
    } else if (initialFile) {
      // Set initial file if provided
      setSelectedFile(initialFile);
      setError('');
      
      // Create preview URL for initial file
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(initialFile);
    }
  }, [isOpen, initialFile]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file.');
      return;
    }

    // Validate file size (max 20MB)
    if (file.size > 20 * 1024 * 1024) {
      setError('File size must be less than 20MB.');
      return;
    }

    setError('');
    setSelectedFile(file);

    // Create preview URL
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewUrl(e.target?.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    try {
      setUploading(true);
      setUploadProgress(0);

      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return prev + 10;
        });
      }, 200);

      await onUpload(selectedFile);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      
      // Close modal after successful upload
      setTimeout(() => {
        onCancel();
      }, 1000);

    } catch (error) {
      setError('Upload failed. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        setSelectedFile(file);
        setError('');
        
        // Create preview URL
        const reader = new FileReader();
        reader.onload = (e) => {
          setPreviewUrl(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please select an image file.');
      }
    }
  };

  const handleBrowseClick = () => {
    fileInputRef.current?.click();
  };

  if (!isOpen) return null;

  return (
    <div className="upload-modal">
      <div className="upload-modal__overlay" onClick={onCancel} />
      <div className="upload-modal__box">
        <div className="upload-modal__header">
          <h2>Upload Image for {characterName}</h2>
          <button className="upload-modal__close" onClick={onCancel}>
            ×
          </button>
        </div>

        <div className="upload-modal__content">
          {!selectedFile ? (
            <div 
              className="upload-modal__drop-zone"
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              onClick={handleBrowseClick}
            >
              <div className="upload-modal__drop-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="#55c57a">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
              </div>
              <p className="upload-modal__drop-text">
                Drag and drop an image here, or click to browse
              </p>
              <p className="upload-modal__drop-hint">
                Supports: JPG, PNG, GIF, WEBP (max 20MB)
              </p>
            </div>
          ) : (
            <div className="upload-modal__preview">
              <div className="upload-modal__preview-image">
                {previewUrl && <img src={previewUrl} alt="Preview" />}
              </div>
              <div className="upload-modal__file-info">
                <p className="upload-modal__file-name">{selectedFile.name}</p>
                <p className="upload-modal__file-size">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
          )}

          {error && (
            <div className="upload-modal__error">
              {error}
            </div>
          )}

          {uploading && (
            <div className="upload-modal__progress">
              <div className="upload-modal__progress-bar">
                <div 
                  className="upload-modal__progress-fill"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="upload-modal__progress-text">
                Uploading... {uploadProgress}%
              </p>
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </div>

        <div className="upload-modal__actions">
          <button 
            className="btn btn--gray upload-modal__cancel"
            onClick={onCancel}
            disabled={uploading}
          >
            Cancel
          </button>
          <button 
            className="btn btn--green upload-modal__upload"
            onClick={handleUpload}
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UploadImageModal; 